import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import * as api from '../../../services/api'
import { useCore } from '../../../hooks/useCore'
import { LoadingPage } from '../../../components/LoadingPage'
import CreateOrUpdateUserWebForm, { UserWebProps } from './CreateOrUpdateUserWebForm'

type AddressFields = {
  cep?: string | null
  street?: string | null
  number_address?: string | null
  complement?: string | null
  district?: string | null
  city?: string | null
  uf?: string | null
}

type RetrieveUserWebResponse = {
  content: AddressFields & {
    id?: string
    first_name?: string
    last_name?: string
    email?: string
    cpf?: string
    phone?: string
    company?: AddressFields
  }
}

function mapContentToUserWebProps(content: RetrieveUserWebResponse['content']): UserWebProps {
  // Para usuários PJ, o endereço vem em content.company (content.* fica null nesse caso).
  const address: AddressFields = content.company ?? content

  return {
    id: content.id,
    person_type: content.cpf ? 'pf' : 'pj',
    first_name: content.first_name,
    last_name: content.last_name,
    email: content.email,
    cpf: content.cpf,
    phone: content.phone,
    cep: address.cep ?? undefined,
    street: address.street ?? undefined,
    number_address: address.number_address ?? undefined,
    complement: address.complement ?? undefined,
    district: address.district ?? undefined,
    city: address.city ?? undefined,
    uf: address.uf ?? undefined,
  }
}

export default function CreateOrUpdateUserWeb(): JSX.Element {
  const { id: userId } = useParams()
  const navigate = useNavigate()
  const { setTitleBar, setPathTitleBar } = useCore()
  const [loading, setLoading] = useState(Boolean(userId))
  const [user, setUser] = useState<UserWebProps | undefined>(undefined)

  useEffect(() => {
    setPathTitleBar(undefined)

    if (!userId) {
      setTitleBar('Novo usuário web')
      setUser(undefined)
      setLoading(false)
      return
    }

    async function fetchUser() {
      try {
        const response = await api.get<RetrieveUserWebResponse>(`user/retrieve/${userId}/`)
        const mappedUser = mapContentToUserWebProps(response.content)
        setUser(mappedUser)
        setTitleBar(`Usuário Web - ${mappedUser.first_name ?? ''} ${mappedUser.last_name ?? ''}`)
        setLoading(false)
      } catch (error) {
        console.error(error)
        navigate('/new-user-web', { replace: true })
      }
    }

    fetchUser()
  }, [userId, setTitleBar, setPathTitleBar, navigate])

  if (loading) return <LoadingPage />

  return (
    <CreateOrUpdateUserWebForm
      user={user}
      onCreated={(id) => navigate(`/user-web/${id}`)}
    />
  )
}
